import React, { useEffect, useRef } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import "../style.scss";
import CompanyDetailStepIcon from "./../../../../assets/component-icon/PersonalDetailStepIcon";
import SignatoryDetailStepIcon from "./../../../../assets/component-icon/ContactDetailStepIcon";
import UnitDetailStepIcon from "./../../../../assets/component-icon/ProfessionalDetailStepIcon";
import { setReviewActiveStep } from "../../new-booking-form.slice";
import AuthorizedSignatoryDetailsReview from "./AuthorizedSignatoryDetailsReview";
import ReviewCompanyDetails from "./ReviewCompanyDetails";
import UnitDetailStep from "./UnitDetailStep";
import { useTranslation } from "react-i18next";
import ApplicantReviewCard from "./ApplicantReviewCard";
import { BOOKING_AS } from "../../../../config/constant";
import StepperIconRoot from "../../../../shared-components/stepper/StepperIconRoot";

const ReviewDetails = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { reviewActiveStep, applicantData } = useSelector(
    (state) => state?.newBookingForm
  );
  const isIndividual = applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL;
  const stepsReviewDetails = isIndividual
    ? t("reviewYourDetails.individual.steps", { returnObjects: true })
    : t("reviewYourDetails.corporate.steps", { returnObjects: true });
  const isMobile = useMediaQuery("(max-width: 767px)");
  const stepperRef = useRef(null);

  useEffect(() => {
    if (isMobile && stepperRef.current) {
      if (reviewActiveStep === stepsReviewDetails.length - 1) {
        // Scroll to the right when the last step is active
        stepperRef.current.scrollTo({
          left: stepperRef.current.scrollWidth,
          behavior: "smooth",
        });
      } else {
        // Scroll back to the left for other steps
        stepperRef.current.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewActiveStep, isMobile]);

  function ColorlibStepIcon(props) {
    const { active, className, icon } = props;

    const isCompleted1 = icon === 1 && reviewActiveStep !== 0;

    const isCompleted2 = icon === 2 && reviewActiveStep !== 1;

    const isCompleted3 = icon === 3 && reviewActiveStep !== 2;

    const icons = {
      1: <CompanyDetailStepIcon show={active} completed={isCompleted1} />,
      2: <SignatoryDetailStepIcon show={active} completed={isCompleted2} />,
      3: <UnitDetailStepIcon show={active} completed={isCompleted3} />,
    };

    return (
      <StepperIconRoot
        ownerState={{
          active,
          isCompleted1,
          isCompleted2,
          isCompleted3,
          variant: "colorlib", // use "colorlib" for this style
        }}
        className={className}
      >
        {icons[icon]}
      </StepperIconRoot>
    );
  }

  const handleStep = (step) => () => {
    dispatch(setReviewActiveStep(step));
  };

  return (
    <>
      <Typography
        sx={{
          color: "#1C252E",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "36px",
          mb: "12px",
          textAlign: "center",
        }}
      >
        {t("reviewYourDetails.title")}
      </Typography>
      <Box sx={{ width: "100%" }}>
        <Stepper
          activeStep={reviewActiveStep}
          sx={{ mb: 3 }}
          ref={stepperRef}
          className="steeperReview"
        >
          {stepsReviewDetails.map((label, index) => {
            return (
              <Step
                key={label}
                onClick={handleStep(index)}
                sx={{
                  "& .MuiStepLabel-root": { cursor: "pointer" },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: "#1A407D",
                    fontSize: "14px",
                    fontWeight: "600",
                    lineHeight: "22px",
                  },
                }}
              >
                <StepLabel StepIconComponent={ColorlibStepIcon}>
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
      {(reviewActiveStep === 0 || reviewActiveStep === -1) &&
        (isIndividual ? <ApplicantReviewCard /> : <ReviewCompanyDetails />)}
      {reviewActiveStep === 1 &&
        (isIndividual ? (
          <UnitDetailStep />
        ) : (
          <AuthorizedSignatoryDetailsReview />
        ))}
      {reviewActiveStep === 2 && !isIndividual && <UnitDetailStep />}
    </>
  );
};

export default ReviewDetails;
